/**
 * Pure analytics aggregator for cleaning inspections.
 *
 * Takes the raw inspection collection + the checklist schema and returns
 * everything the Analytics tab needs: totals, 30-day count, average score,
 * per-category pass rates, and the top 5 most frequently failed items.
 *
 * Lives separately from CleaningInspection.js so the page component can stay
 * focused on rendering + event handling, and so this calc is easy to unit-test.
 */
import { calculateScore } from './inspectionScoring';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export function getAnalyticsData(inspections, inspectionChecklist) {
  const completed = inspections.filter(
    (i) => i.status === 'completed' || i.status === 'approved'
  );

  const thirtyDaysAgo = Date.now() - 30 * MS_PER_DAY;
  const last30Days = inspections.filter((i) => {
    const t = new Date(i.scheduledDate).getTime();
    return Number.isFinite(t) && t >= thirtyDaysAgo;
  });

  // Pass rate per category — single pass over completed × items.
  const categoryStats = {};
  for (const cat of Object.keys(inspectionChecklist)) {
    let passed = 0;
    let total = 0;
    for (const inspection of completed) {
      for (const item of inspectionChecklist[cat].items) {
        const result = inspection.checklist?.[cat]?.[item.id];
        if (result?.status && result.status !== 'na') {
          total++;
          if (result.status === 'pass') passed++;
        }
      }
    }
    categoryStats[cat] = {
      label: inspectionChecklist[cat].label,
      passRate: total > 0 ? Math.round((passed / total) * 100) : 0,
      total,
    };
  }

  // Average score across completed inspections.
  const scores = [];
  for (const i of completed) {
    const s = calculateScore(i, inspectionChecklist);
    if (s !== null) scores.push(s);
  }
  const avgScore =
    scores.length > 0
      ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length)
      : 0;

  // Top 5 most-failed items.
  const failedItems = new Map(); // key: `${cat}|${item.id}` → {name, category, count}
  for (const inspection of completed) {
    for (const cat of Object.keys(inspectionChecklist)) {
      for (const item of inspectionChecklist[cat].items) {
        const result = inspection.checklist?.[cat]?.[item.id];
        if (result?.status !== 'fail') continue;
        const key = `${cat}|${item.id}`;
        const existing = failedItems.get(key);
        if (existing) {
          existing.count++;
        } else {
          failedItems.set(key, {
            name: item.name,
            category: inspectionChecklist[cat].label,
            count: 1,
          });
        }
      }
    }
  }
  const topIssues = Array.from(failedItems.values())
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  return {
    totalInspections: inspections.length,
    completedCount: completed.length,
    last30DaysCount: last30Days.length,
    avgScore,
    categoryStats,
    topIssues,
    pendingCount: inspections.filter((i) => i.status === 'pending').length,
    inProgressCount: inspections.filter((i) => i.status === 'in_progress').length,
  };
}
